import React, { useCallback, useEffect, useState } from 'react';
import type { MutableRefObject } from 'react';

import { FormatOptions } from '../../../../../common/table/CellTypeConstants';
import { isCellTypeDateTime } from '../../../../../common/table/utils/tableCellDataPropertyUtils';
import getEditorJsonText from '../../../../../common/tiptap/utils/jsonContentUtils/getEditorJsonText';
import { getDateFromString, getStartOfDayDate, toNumericDateTimeString } from '../../../../../common/utils/timeUtil';
import type { MNHotInstance } from '../../bridge/tableHotInstanceTypes';
import { isFormula } from '../../bridge/utils/dataUtils/formula/tableFormulaPredicateUtils';
import { getHotTableCellData } from '../../bridge/utils/hotInstanceUtils/meta/hotTableCellMetaUtils';
import { isHotInstanceAvailable } from '../../bridge/utils/hotInstanceUtils/hotTableInstanceValidationUtils';
import { isSelectingSingleSelectionAndCell } from '../../bridge/utils/selectionUtils/tableSelectionPredicateUtils';
import { getCellRenderValue } from '../utils/dataUtils/cell/tableCellRenderValueUtils';

type TableDatePickerProps = {
    locale: string;
    showFormulaPicker: boolean;
    isCurrentlyEditingTableCell: boolean;
    hotTableInstanceRef: MutableRefObject<MNHotInstance | null>;
};

/**
 * Returns the selected cell data and its stable key while a single cell is being edited.
 */
const getEditingCell = (hotInstance: MNHotInstance | null) => {
    if (!isHotInstanceAvailable(hotInstance)) return null;

    const currentCellSelections = hotInstance.getSelected();
    if (!isSelectingSingleSelectionAndCell(currentCellSelections)) return null;

    const [cellSelection] = currentCellSelections;
    const cellCoords = {
        row: Math.max(cellSelection[0], 0),
        col: Math.max(cellSelection[1], 0),
    };

    const cellData = getHotTableCellData(hotInstance, cellCoords);
    if (!cellData) return null;

    return { cellKey: `${cellCoords.row}:${cellCoords.col}`, cellData };
};

const useTableDatePicker = ({
    isCurrentlyEditingTableCell,
    locale,
    showFormulaPicker,
    hotTableInstanceRef,
}: TableDatePickerProps) => {
    const { cellKey, cellData } = (isCurrentlyEditingTableCell && getEditingCell(hotTableInstanceRef?.current)) || {};
    const rawText = cellData?.draftContent ? getEditorJsonText(cellData.draftContent) : (cellData?.value ?? '');
    const dateTimeTypeObject = isCellTypeDateTime(cellData) ? cellData.type : undefined;

    const [hasTimeValue, setHasTimeValue] = useState(dateTimeTypeObject?.[FormatOptions.HAS_TIME] ?? false);

    const [calendarInputDate, setCalendarInputDate] = useState(
        () => getDateFromString(rawText, locale) || getStartOfDayDate(new Date()),
    );

    const showDatePicker =
        isCurrentlyEditingTableCell && !!dateTimeTypeObject && !showFormulaPicker && !isFormula(rawText);

    // Reset picker state when editing moves to a different table cell.
    useEffect(() => {
        if (!cellKey) return;

        setHasTimeValue(dateTimeTypeObject?.[FormatOptions.HAS_TIME] ?? false);
        setCalendarInputDate(getDateFromString(rawText, locale) || getStartOfDayDate(new Date()));
    }, [cellKey, dateTimeTypeObject, locale, rawText]);

    const updateEditorContent = useCallback(
        (date: Date, newHasTimeValue: boolean) => {
            if (!showDatePicker || !dateTimeTypeObject) return;

            const hotInstance = hotTableInstanceRef.current;
            if (!isHotInstanceAvailable(hotInstance)) return;

            const cellEditor = hotInstance.getMNEditor();
            if (!cellEditor) return;

            const newDateTimeTypeObject = {
                ...dateTimeTypeObject,
                [FormatOptions.HAS_TIME]: newHasTimeValue,
            };

            const newDateTimeValue = toNumericDateTimeString(
                date,
                {
                    hasTime: newDateTimeTypeObject[FormatOptions.HAS_TIME],
                    hasSeconds: newDateTimeTypeObject[FormatOptions.HAS_SECONDS],
                    useTwelveHour: false,
                },
                locale,
            );

            const formattedDateTimeString = getCellRenderValue(
                { value: newDateTimeValue, type: newDateTimeTypeObject },
                locale,
            );

            cellEditor.setPlainTextContent(formattedDateTimeString, cellData?.textStyle || []);
        },
        [cellData, dateTimeTypeObject, hotTableInstanceRef, locale, showDatePicker],
    );

    const updateDate = useCallback(
        (date: Date) => {
            setCalendarInputDate(date);
            updateEditorContent(date, hasTimeValue);
        },
        [hasTimeValue, updateEditorContent],
    );

    const updateHasTime = useCallback(
        (newHasTime: boolean) => {
            setHasTimeValue(newHasTime);
            updateEditorContent(calendarInputDate, newHasTime);
        },
        [calendarInputDate, updateEditorContent],
    );

    return {
        showDatePicker,
        dateTimePickerProps: {
            locale,
            date: calendarInputDate,
            updateDate,
            hasTime: hasTimeValue,
            updateDateHasTime: updateHasTime,
        },
    };
};

export default useTableDatePicker;
